import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    ActivityIndicator,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { DateRangeFilterModal } from '../components/DateRangeFilterModal';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import DetailScreenLoading from '../components/DetailScreenLoading';
import ApiService from '../services/ApiService';
import { generateCustomerBillPDF } from '../services/PDFService';
import { Customer, DairyRecord, CustomerTransaction } from '../models/types';
import {
    formatCurrency,
    formatNumber,
    formatDate,
} from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'purchase' | 'sale' | 'payment';

const CustomerDetailScreen = ({ navigation, route }: any) => {
    const customer = route.params?.customer as Customer;
    const { user } = useAuth();

    const [dairyRecords, setDairyRecords] = useState<DairyRecord[]>([]);
    const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
    const [balance, setBalance] = useState({ totalCredit: 0, totalPaid: 0, balance: 0, advance: 0 });
    const [selectedTab, setSelectedTab] = useState<TabType>(customer.customerType || 'sale');
    const [isInfoExpanded, setIsInfoExpanded] = useState(false);

    // Date filter state
    const [dateFilterVisible, setDateFilterVisible] = useState(false);
    const [filterFromDate, setFilterFromDate] = useState<string | null>(null);
    const [filterToDate, setFilterToDate] = useState<string | null>(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [pendingPDFDownload, setPendingPDFDownload] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
        confirmText?: string;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const loadData = async (fromDate?: string, toDate?: string) => {
        try {
            // Load dairy records for this customer with optional date filter
            const records = await ApiService.getCustomerDairyRecords(
                customer.id,
                fromDate || undefined,
                toDate || undefined
            );
            setDairyRecords(records);

            // Load payment transactions
            const txns = await ApiService.getCustomerTransactions(customer.id);
            setTransactions(txns);

            // Load balance
            const balanceData = await ApiService.getCustomerBalance(customer.id);
            setBalance(balanceData);
        } catch (error) {
            console.error('Error loading customer data:', error);
        } finally {
            setInitialLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadData(filterFromDate || undefined, filterToDate || undefined);
        }, [customer.id, filterFromDate, filterToDate])
    );

    // Handle date filter apply
    const handleApplyDateFilter = async (fromDate: string, toDate: string) => {
        setFilterFromDate(fromDate);
        setFilterToDate(toDate);
        setDateFilterVisible(false);

        // If there's a pending PDF download, generate it now
        if (pendingPDFDownload) {
            setPendingPDFDownload(false);
            // Small delay to let the modal close and data update
            setTimeout(() => {
                handleDownloadPDFWithRange(fromDate, toDate);
            }, 300);
        }
    };

    // Handle clear date filter
    const handleClearDateFilter = () => {
        setFilterFromDate(null);
        setFilterToDate(null);
    };

    // Open filter modal for download
    const handleDownloadClick = () => {
        setPendingPDFDownload(true);
        setDateFilterVisible(true);
    };

    // Handle PDF generation with specific date range
    const handleDownloadPDFWithRange = async (fromDate: string, toDate: string) => {
        setIsGeneratingPDF(true);
        try {
            // Fetch records for the specific date range
            const filteredRecords = await ApiService.getCustomerDairyRecords(
                customer.id,
                fromDate,
                toDate
            );

            const filePath = await generateCustomerBillPDF({
                customer,
                records: filteredRecords,
                balance,
                dateRange: { from: fromDate, to: toDate },
            });

            if (filePath) {
                const fileName = filePath.split('/').pop();
                const location = filePath.includes('/Download/')
                    ? 'Downloads folder'
                    : 'device storage';
                setModal({
                    visible: true,
                    type: 'success',
                    title: '✓ PDF Saved',
                    message: `Your bill has been saved to ${location}.\n\nFile: ${fileName}`,
                });
            } else {
                setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to generate PDF. No file was created.' });
            }
        } catch (error: any) {
            console.error('Error generating PDF:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: `Failed to generate PDF: ${error?.message || 'Unknown error'}`
            });
        } finally {
            setIsGeneratingPDF(false);
            setLoading(false);
        }
    };

    const handleDeleteCustomer = () => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Customer',
            message: `Are you sure you want to delete "${customer.name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            onConfirm: async () => {
                setModal(prev => ({ ...prev, visible: false }));
                setLoading(true);
                try {
                    await ApiService.deleteCustomer(customer.id);
                    navigation.goBack();
                } catch (error) {
                    console.error('Error deleting customer:', error);
                    setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to delete customer' });
                    setLoading(false);
                }
            },
        });
    };

    const handleDeleteRecord = (record: DairyRecord) => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Record',
            message: 'Are you sure you want to delete this record?',
            confirmText: 'Delete',
            onConfirm: async () => {
                setModal(prev => ({ ...prev, visible: false }));
                setLoading(true);
                try {
                    await ApiService.deleteDairyRecord(record.id);
                    await loadData();
                } catch (error) {
                    console.error('Error deleting record:', error);
                    setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to delete record' });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleEditRecord = (record: DairyRecord) => {
        navigation.navigate('AddCustomerTransaction', {
            customer,
            existingRecord: record,
            transactionType: record.recordType
        });
    };
    // Get icon for record type
    const getTabIcon = (type: TabType) => {
        switch (type) {
            case 'purchase': return { name: 'cart-arrow-down', color: '#FF9800' };
            case 'sale': return { name: 'cart-arrow-up', color: '#4CAF50' };
            case 'payment': return { name: 'cash', color: theme.colors.primary };
            default: return { name: 'file-document', color: theme.colors.textSecondary };
        }
    };

    const renderRecordItem = ({ item }: { item: DairyRecord }) => {
        const icon = getTabIcon(item.recordType as TabType);

        const getStatusColor = () => {
            switch (item.paymentStatus) {
                case 'paid': return theme.colors.success;
                case 'partial': return theme.colors.warning;
                case 'pending': return theme.colors.error;
                default: return theme.colors.textSecondary;
            }
        };

        return (
            <TouchableOpacity
                style={[styles.recordCard, { borderLeftColor: icon.color }]}
                onPress={() => handleEditRecord(item)}
                activeOpacity={0.7}
            >
                <View style={[styles.recordIcon, { backgroundColor: item.recordType === 'payment' ? '#E3F2FD' : icon.color + '18' }]}>
                    <Icon name={icon.name} size={24} color={item.recordType === 'payment' ? '#2196F3' : icon.color} />
                </View>

                <View style={styles.recordContent}>
                    <View style={styles.recordTitleRow}>
                        <Text style={styles.recordTitle}>
                            {item.recordType === 'payment' ? 'Payment' : `${formatNumber(item.quantity)} L`}
                        </Text>
                        {item.paymentStatus && (
                            <View style={[styles.statusBadge, { backgroundColor: getStatusColor() + '20' }]}>
                                <Text style={[styles.statusBadgeText, { color: getStatusColor() }]}>
                                    {item.paymentStatus.toUpperCase()}
                                </Text>
                            </View>
                        )}
                    </View>
                    <Text style={styles.recordDate}>{formatDate(item.date)}</Text>
                    {item.notes && (
                        <Text style={styles.recordNotes} numberOfLines={1}>{item.notes}</Text>
                    )}
                </View>

                <View style={styles.recordRightSection}>
                    <Text style={[styles.recordValue, { color: item.recordType === 'payment' ? '#2196F3' : icon.color }]}>
                        {formatCurrency(item.recordType === 'payment' ? (item.amountPaid || 0) : (item.totalAmount || 0))}
                    </Text>
                    {(item.pendingAmount ?? 0) > 0 && (
                        <Text style={styles.pendingText}>
                            Pending: {formatCurrency(item.pendingAmount || 0)}
                        </Text>
                    )}
                    <TouchableOpacity
                        style={styles.deleteButton}
                        onPress={() => handleDeleteRecord(item)}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                        <Icon name="trash-can-outline" size={18} color={theme.colors.error} />
                    </TouchableOpacity>
                </View>
            </TouchableOpacity>
        );
    };

    const renderPaymentItem = ({ item }: { item: CustomerTransaction }) => {
        return (
            <View style={[styles.recordCard, { borderLeftColor: '#2196F3' }]}>
                <View style={[styles.recordIcon, { backgroundColor: '#E3F2FD' }]}>
                    <Icon name="cash" size={24} color="#2196F3" />
                </View>

                <View style={styles.recordContent}>
                    <Text style={styles.recordTitle}>Payment</Text>
                    <Text style={styles.recordDate}>{formatDate(item.date)}</Text>
                    {item.notes && (
                        <Text style={styles.recordNotes} numberOfLines={1}>{item.notes}</Text>
                    )}
                </View>

                <View style={styles.recordRightSection}>
                    <Text style={[styles.recordValue, { color: theme.colors.success }]}>
                        + {formatCurrency(item.amount)}
                    </Text>
                </View>
            </View>
        );
    };

    const renderEmptyRecords = () => (
        <View style={styles.emptyContainer}>
            <Icon
                name={getTabIcon(selectedTab).name}
                size={60}
                color={theme.colors.textLight}
            />
            <Text style={styles.emptyText}>
                No {selectedTab} records yet
            </Text>
        </View>
    );

    const typeColor = customer.customerType === 'sale' ? '#4CAF50' : '#FF9800';

    if (initialLoading) {
        return (
            <SafeAreaView style={styles.container} edges={['top']}>
                <ScreenHeader
                    title="Customer Details"
                    showBackButton={true}
                    navigation={navigation}
                />
                <DetailScreenLoading showMessage={false} />
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title="Customer Details"
                showBackButton={true}
                navigation={navigation}
            />

            <ScrollView showsVerticalScrollIndicator={false}>
                {/* Customer Header Card */}
                <View style={styles.headerCard}>
                    <View style={styles.headerTop}>
                        <View style={[styles.customerIconContainer, { backgroundColor: typeColor }]}>
                            <Icon
                                name={customer.customerType === 'sale' ? 'account' : 'account-tie'}
                                size={36}
                                color="#FFFFFF"
                            />
                        </View>
                        <View style={styles.customerHeaderInfo}>
                            <Text style={styles.customerName}>{customer.name}</Text>
                            {customer.phone && (
                                <Text style={styles.customerPhone}>{customer.phone}</Text>
                            )}
                        </View>
                        <View style={[styles.typeBadge, { backgroundColor: typeColor + '20', borderColor: typeColor }]}>
                            <Text style={[styles.typeText, { color: typeColor }]}>
                                {customer.customerType === 'sale' ? 'BUYER' : 'SUPPLIER'}
                            </Text>
                        </View>
                    </View>

                    {/* Action Icons Row */}
                    <View style={styles.headerActions}>
                        <TouchableOpacity
                            style={styles.headerActionBtn}
                            onPress={() => navigation.navigate('AddEditCustomer', { customer })}
                            activeOpacity={0.7}
                        >
                            <Icon name="pencil-outline" size={18} color={theme.colors.primary} />
                            <Text style={styles.headerActionText}>Edit</Text>
                        </TouchableOpacity>
                        <View style={styles.headerActionDivider} />
                        <TouchableOpacity
                            style={styles.headerActionBtn}
                            onPress={handleDeleteCustomer}
                            activeOpacity={0.7}
                        >
                            <Icon name="trash-can-outline" size={18} color={theme.colors.error} />
                            <Text style={[styles.headerActionText, { color: theme.colors.error }]}>Delete</Text>
                        </TouchableOpacity>
                    </View>

                    {/* Download PDF Button */}


                    {/* Active Filter Indicator */}
                    {filterFromDate && filterToDate && (
                        <View style={styles.filterIndicator}>
                            <Icon name="filter" size={14} color={theme.colors.primary} />
                            <Text style={styles.filterIndicatorText}>
                                {formatDate(filterFromDate)} - {formatDate(filterToDate)}
                            </Text>
                            <TouchableOpacity onPress={handleClearDateFilter} activeOpacity={0.7}>
                                <Icon name="close-circle" size={18} color={theme.colors.textSecondary} />
                            </TouchableOpacity>
                        </View>
                    )}
                </View>

                {/* Additional Information - Collapsible */}
                <View style={styles.infoCard}>
                    <TouchableOpacity
                        style={styles.infoCardHeader}
                        onPress={() => setIsInfoExpanded(!isInfoExpanded)}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.cardTitle}>Additional Information</Text>
                        <Icon
                            name={isInfoExpanded ? 'chevron-up' : 'chevron-down'}
                            size={24}
                            color={theme.colors.textSecondary}
                        />
                    </TouchableOpacity>

                    {isInfoExpanded && (
                        <View style={styles.infoGrid}>
                            {customer.createdAt && (
                                <View style={styles.infoItem}>
                                    <View style={styles.infoIconContainer}>
                                        <Icon name="calendar" size={18} color={theme.colors.primary} />
                                    </View>
                                    <View style={styles.infoContent}>
                                        <Text style={styles.infoLabel}>Added on</Text>
                                        <Text style={styles.infoValue}>{formatDate(customer.createdAt)}</Text>
                                    </View>
                                </View>
                            )}
                            {customer.address && (
                                <View style={styles.infoItem}>
                                    <View style={styles.infoIconContainer}>
                                        <Icon name="map-marker" size={18} color={theme.colors.primary} />
                                    </View>
                                    <View style={styles.infoContent}>
                                        <Text style={styles.infoLabel}>Address</Text>
                                        <Text style={styles.infoValue}>{customer.address}</Text>
                                    </View>
                                </View>
                            )}
                            {customer.notes && (
                                <View style={styles.notesContainer}>
                                    <View style={styles.notesHeader}>
                                        <Icon name="note-text" size={18} color={theme.colors.primary} />
                                        <Text style={styles.notesLabel}>Notes</Text>
                                    </View>
                                    <Text style={styles.notesText}>{customer.notes}</Text>
                                </View>
                            )}

                            {/* Download PDF Button */}
                            <TouchableOpacity
                                style={styles.downloadPdfBtn}
                                onPress={handleDownloadClick}
                                activeOpacity={0.7}
                                disabled={isGeneratingPDF}
                            >
                                {isGeneratingPDF ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <Icon name="file-download-outline" size={20} color="#FFFFFF" />
                                )}
                                <Text style={styles.downloadPdfBtnText}>
                                    {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>

                {/* Account Summary */}
                <View style={styles.summaryCard}>
                    <Text style={styles.summaryTitle}>Account Summary</Text>

                    {/* Total Amount Row */}
                    <View style={styles.summaryRow}>
                        <View style={[styles.summaryIconBg, { backgroundColor: '#E3F2FD' }]}>
                            <Icon name="file-document" size={20} color="#2196F3" />
                        </View>
                        <Text style={styles.summaryLabel}>Total Amount</Text>
                        <Text style={styles.summaryValue}>{formatCurrency(balance.totalCredit)}</Text>
                    </View>

                    <View style={styles.summaryDivider} />

                    {/* Amount Paid Row */}
                    <View style={styles.summaryRow}>
                        <View style={[styles.summaryIconBg, { backgroundColor: '#E8F5E9' }]}>
                            <Icon name="cash-check" size={20} color="#4CAF50" />
                        </View>
                        <Text style={styles.summaryLabel}>Amount Paid</Text>
                        <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                            {formatCurrency(balance.totalPaid)}
                        </Text>
                    </View>

                    <View style={styles.summaryDivider} />

                    {/* Pending Balance Row */}
                    <View style={styles.summaryRow}>
                        <View style={[styles.summaryIconBg, { backgroundColor: balance.balance > 0 ? '#FFEBEE' : '#E8F5E9' }]}>
                            <Icon name="clock-outline" size={20} color={balance.balance > 0 ? '#E53935' : '#4CAF50'} />
                        </View>
                        <Text style={styles.summaryLabel}>Pending Balance</Text>
                        <Text style={[styles.summaryValue, { color: balance.balance > 0 ? '#E53935' : '#4CAF50', fontWeight: '700' }]}>
                            {formatCurrency(balance.balance)}
                        </Text>
                    </View>

                    {balance.advance > 0 && (
                        <>
                            <View style={styles.summaryDivider} />
                            <View style={styles.summaryRow}>
                                <View style={[styles.summaryIconBg, { backgroundColor: '#E8F5E9' }]}>
                                    <Icon name="wallet-plus" size={20} color="#4CAF50" />
                                </View>
                                <Text style={styles.summaryLabel}>Advance</Text>
                                <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                                    {formatCurrency(balance.advance)}
                                </Text>
                            </View>
                        </>
                    )}
                </View>

                {/* Records Section */}
                <View style={styles.recordsSection}>
                    <View style={styles.recordsHeader}>
                        <Text style={styles.sectionTitle}>Records</Text>
                        <TouchableOpacity
                            style={styles.addRecordButton}
                            onPress={() => navigation.navigate('AddCustomerTransaction', {
                                customer,
                                transactionType: selectedTab
                            })}
                        >
                            <Icon name="plus" size={20} color={theme.colors.primary} />
                            <Text style={styles.addRecordText}>Add Record</Text>
                        </TouchableOpacity>
                    </View>

                    {/* Tabs */}
                    <View style={styles.tabs}>
                        {((customer.customerType === 'purchase' ? ['purchase', 'payment'] : ['sale', 'payment']) as TabType[]).map(tab => {
                            const icon = getTabIcon(tab);
                            const isActive = selectedTab === tab;
                            const count = tab === 'payment'
                                ? transactions.length
                                : dairyRecords.filter(r => r.recordType === tab).length;

                            return (
                                <TouchableOpacity
                                    key={tab}
                                    style={[styles.tab, isActive && styles.tab_active]}
                                    onPress={() => setSelectedTab(tab)}
                                    activeOpacity={0.7}
                                >
                                    <Icon
                                        name={icon.name}
                                        size={20}
                                        color={isActive ? theme.colors.primary : theme.colors.textSecondary}
                                    />
                                    <Text style={[
                                        styles.tabText,
                                        isActive && styles.tabTextActive
                                    ]}>
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)} ({count})
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>

                    {/* Records List */}
                    <FlashList
                        data={(selectedTab === 'payment' ? transactions : dairyRecords.filter(r => r.recordType === selectedTab)) as (DairyRecord | CustomerTransaction)[]}
                        renderItem={({ item }) => {
                            if (selectedTab === 'payment') {
                                return renderPaymentItem({ item: item as CustomerTransaction });
                            }
                            return renderRecordItem({ item: item as DairyRecord });
                        }}
                        keyExtractor={item => item.id}
                        ListEmptyComponent={renderEmptyRecords}
                        scrollEnabled={false}
                    />
                </View>

                <View style={styles.bottomPadding} />
            </ScrollView>

            {/* Date Range Filter Modal */}
            <DateRangeFilterModal
                visible={dateFilterVisible}
                onClose={() => setDateFilterVisible(false)}
                onApply={handleApplyDateFilter}
                initialFromDate={filterFromDate || undefined}
                initialToDate={filterToDate || undefined}
            />

            {/* General Modal */}
            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                confirmText={modal.confirmText || 'OK'}
            />

            <LoadingOverlay
                visible={loading || isGeneratingPDF}
                message={isGeneratingPDF ? "Generating PDF..." : "Loading..."}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    // Header Card - Matching Cow Detail
    headerCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        marginBottom: 0,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    headerTop: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    customerIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 28,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    customerHeaderInfo: {
        flex: 1,
    },
    customerName: {
        ...theme.typography.h4,
        color: theme.colors.text,
        fontWeight: '700',
    },
    customerPhone: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
    },
    typeBadge: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.round,
        borderWidth: 1,
    },
    typeText: {
        ...theme.typography.caption,
        fontWeight: '700',
        fontSize: 11,
    },
    headerActions: {
        flexDirection: 'row',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        justifyContent: 'center',
    },
    headerActionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.xs,
        paddingHorizontal: theme.spacing.lg,
        gap: theme.spacing.xs,
    },
    headerActionText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    headerActionDivider: {
        width: 1,
        height: 20,
        backgroundColor: theme.colors.border,
    },
    // Download PDF Button
    downloadPdfBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.lg,
        backgroundColor: theme.colors.primary,
        borderRadius: theme.borderRadius.md,
        gap: theme.spacing.sm,
    },
    downloadPdfBtnText: {
        ...theme.typography.body2,
        color: '#FFFFFF',
        fontWeight: '600',
    },
    filterIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        paddingHorizontal: theme.spacing.md,
        backgroundColor: theme.colors.primary + '15',
        borderRadius: theme.borderRadius.round,
        gap: theme.spacing.xs,
        alignSelf: 'center',
    },
    filterIndicatorText: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontWeight: '600',
    },

    // Info Card - Matching Cow Detail
    infoCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    infoCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
    },
    infoGrid: {
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    infoItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.primary + '15',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    infoContent: {
        flex: 1,
    },
    infoLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    infoValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    notesContainer: {
        marginTop: theme.spacing.lg,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.md,
    },
    notesHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.xs,
    },
    notesLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.xs,
        fontWeight: '600',
    },
    notesText: {
        ...theme.typography.body2,
        color: theme.colors.text,
    },

    // Summary Card - Matching Cow Detail
    summaryCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        ...theme.shadows.sm,
    },
    summaryTitle: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.md,
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    summaryIconBg: {
        width: 36,
        height: 36,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    summaryLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        flex: 1,
    },
    summaryValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    summaryDivider: {
        height: 1,
        backgroundColor: theme.colors.border,
        marginLeft: 52,
    },

    // Records Section - Matching Cow Detail
    recordsSection: {
        padding: theme.spacing.lg,
    },
    recordsHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    sectionTitle: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
    },
    addRecordButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary + '20',
        borderRadius: theme.borderRadius.md,
    },
    addRecordText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
        marginLeft: theme.spacing.xs,
    },
    tabs: {
        flexDirection: 'row',
        marginBottom: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.xs,
    },
    tab: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.md,
    },
    tab_active: {
        backgroundColor: theme.colors.background,
    },
    tabText: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.xs,
        fontWeight: '600',
    },
    tabTextActive: {
        color: theme.colors.primary,
    },

    // List Items - Modern
    countBadge: {
        minWidth: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: '#E0E0E0',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 6,
    },
    countBadgeActive: {
        backgroundColor: theme.colors.primary,
    },
    countBadgeText: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        fontWeight: '700',
        fontSize: 11,
    },
    countBadgeTextActive: {
        color: '#FFFFFF',
    },
    recordCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderLeftWidth: 4,
        ...theme.shadows.sm,
    },
    recordIcon: {
        width: 44,
        height: 44,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    recordContent: {
        flex: 1,
        paddingRight: theme.spacing.sm,
    },
    recordTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    recordTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    statusBadge: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    statusBadgeText: {
        ...theme.typography.caption,
        fontSize: 9,
        fontWeight: '700',
    },
    recordDate: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    recordNotes: {
        ...theme.typography.caption,
        color: theme.colors.textLight,
        fontStyle: 'italic',
        marginTop: 2,
    },
    recordRightSection: {
        alignItems: 'flex-end',
    },
    recordValue: {
        ...theme.typography.body1,
        fontWeight: '700',
    },
    pendingText: {
        ...theme.typography.caption,
        color: theme.colors.error,
        marginTop: 2,
    },
    deleteButton: {
        padding: theme.spacing.xs,
        marginTop: 4,
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: theme.spacing.xxl,
    },
    emptyText: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.sm,
    },
    bottomPadding: {
        height: theme.spacing.xxl,
    },
});

export default CustomerDetailScreen;
